import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { Type, Plus, X, ChevronDown, ChevronUp, CheckCircle, XCircle, Clock, Zap } from 'lucide-react';

const TextFieldsNode = ({ data, isConnectable, selected }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isResultsExpanded, setIsResultsExpanded] = useState(false);
  
  // Initialize fields if not present
  const fields = data.fields || [{ name: 'variable1', values: [''] }];
  
  // Parse execution results if available
  let executionResult = null;
  let executionStatus = null;
  
  if (data.executionResult) {
    try {
      executionResult = typeof data.executionResult === 'string' 
        ? JSON.parse(data.executionResult) 
        : data.executionResult;
      executionStatus = data.executionStatus || 'completed';
    } catch (e) {
      console.error('Error parsing execution result:', e);
    }
  }
  
  const getStatusIcon = () => {
    switch (executionStatus) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-celadon-600" />;
      case 'failed':
        return <XCircle className="h-3 w-3 text-gray-500" />;
      case 'running':
        return <Clock className="h-3 w-3 text-gray-500 animate-spin" />;
      default:
        return null;
    }
  };
  
  const getNodeBorderColor = () => {
    if (selected) {
      return 'border-celadon-400 shadow-celadon-200/50 shadow-lg';
    }
    
    switch (executionStatus) {
      case 'completed':
        return 'border-celadon-300 shadow-celadon-100/50';
      case 'failed':
        return 'border-gray-300 shadow-gray-100/50';
      case 'running':
        return 'border-gray-300 shadow-gray-100/50';
      default:
        return 'border-gray-200 shadow-gray-50/50';
    }
  };
  
  const getStatusBadgeColor = () => {
    switch (executionStatus) {
      case 'completed':
        return 'bg-celadon-100 text-celadon-800';
      case 'failed':
        return 'bg-gray-100 text-gray-800';
      case 'running':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`
      bg-white border-2 rounded-2xl shadow-soft min-w-[280px] max-w-[400px] 
      transition-all duration-200 hover:shadow-medium
      ${getNodeBorderColor()}
    `}>
      
      {/* Node Header */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-4 py-3 rounded-t-2xl border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-gray-200 rounded-lg">
              <Type className="h-4 w-4 text-gray-700" />
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-900">
                {data.label || 'TextFields Node'}
              </span>
              <div className="text-xs text-gray-600 opacity-75">
                Variable Inputs
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            )}
          </button>
        </div>
      </div>
      
      {/* Node Content */}
      <div className="p-4">
        {isExpanded && (
          <div className="space-y-4">
            {fields.map((field, fieldIndex) => (
              <div key={fieldIndex} className="space-y-2">
                {/* Field Name */}
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={field.name}
                    placeholder="Variable name"
                    className="flex-1 text-xs font-medium px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-celadon-500"
                    readOnly={true} // Will be editable via NodeDetailsPanel
                  />
                  {fields.length > 1 && (
                    <button
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                
                {/* Field Values */}
                <div className="space-y-1">
                  {field.values.map((value, valueIndex) => (
                    <div key={valueIndex} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={value}
                        placeholder="Enter value"
                        className="flex-1 text-xs px-3 py-2 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-celadon-500 focus:border-celadon-500"
                        readOnly={true} // Will be editable via NodeDetailsPanel
                      />
                      {field.values.length > 1 && (
                        <button
                          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  
                  {/* Add Value Button */}
                  <button
                    className="w-full py-1 text-xs text-gray-600 hover:text-gray-700 hover:bg-gray-50 border border-dashed border-gray-300 rounded transition-colors flex items-center justify-center space-x-1"
                  >
                    <Plus className="h-3 w-3" />
                    <span>Add value</span>
                  </button>
                </div>
              </div>
            ))}
            
            {/* Add Field Button */}
            <button
              className="w-full py-2 text-xs text-gray-600 hover:text-gray-700 hover:bg-gray-50 border border-dashed border-gray-300 rounded transition-colors flex items-center justify-center space-x-1"
            >
              <Plus className="h-3 w-3" />
              <span>Add field</span>
            </button>
          </div>
        )}
        
        {!isExpanded && (
          <div className="text-center text-xs text-gray-500 py-2">
            {fields.length} field{fields.length !== 1 ? 's' : ''} defined
            {executionResult && (
              <div className="text-celadon-600 mt-1">
                {executionResult.output.variableCount} combinations generated
              </div>
            )}
          </div>
        )}

        {/* Execution Results Summary */}
        {executionResult && executionResult.output && (
          <div className="pt-3 border-t border-gray-200">
            <button
              onClick={() => setIsResultsExpanded(!isResultsExpanded)}
              className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-2">
                <Zap className="h-3 w-3 text-celadon-600" />
                <span className="text-xs font-medium text-gray-800">Results</span>
              </div>
              {isResultsExpanded ? (
                <ChevronUp className="h-3 w-3 text-gray-500" />
              ) : (
                <ChevronDown className="h-3 w-3 text-gray-500" />
              )}
            </button>
            
            {isResultsExpanded && (
              <div className="mt-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <div className="font-semibold text-gray-900">{executionResult.output.fieldNames?.length || 0}</div>
                    <div className="text-gray-600">Fields</div>
                  </div>
                  <div className="bg-celadon-50 rounded-lg p-2 text-center">
                    <div className="font-semibold text-celadon-700">{executionResult.output.variableCount || 0}</div>
                    <div className="text-celadon-600">Combinations</div>
                  </div>
                </div>
                
                {/* Show some example combinations */}
                {executionResult.output.combinations && executionResult.output.combinations.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <div className="text-xs font-medium text-gray-700">Sample Combinations:</div>
                    {executionResult.output.combinations.slice(0, 3).map((combo, index) => (
                      <div key={index} className="text-xs bg-gray-50 rounded p-2">
                        {Object.entries(combo.variables).map(([key, value]) => (
                          <span key={key} className="inline-block mr-2">
                            <span className="font-medium text-celadon-700">{key}:</span> {value}
                          </span>
                        ))}
                      </div>
                    ))}
                    {executionResult.output.combinations.length > 3 && (
                      <div className="text-xs text-gray-500 text-center">
                        +{executionResult.output.combinations.length - 3} more...
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Status Bar */}
        <div className="pt-3 mt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {executionStatus ? (
                <span className={`
                  inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                  ${getStatusBadgeColor()}
                `}>
                  {executionStatus === 'running' && <Clock className="h-3 w-3 mr-1 animate-spin" />}
                  {executionStatus === 'completed' ? 'Complete' : executionStatus}
                </span>
              ) : (
                'Ready'
              )}
            </span>
            <div className="flex items-center">
              {getStatusIcon()}
              <div className="w-2 h-2 bg-celadon-400 rounded-full ml-2"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{ 
          background: '#9cc4a5',
          width: '14px',
          height: '14px',
          border: '2px solid #64926e',
          borderRadius: '50%'
        }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default TextFieldsNode; 